'use client';

import { useEffect, useId, useRef, MouseEventHandler } from 'react';
import styles from './navsearch.module.scss';
import { Search } from '@/ui-components/organisms/Search/Search';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { Text } from '@/ui-components/atoms/Text/Text';
import { SearchSuggestions, type SearchChip } from '@/ui-components/molecules/SearchSuggestions/SearchSuggestions';
import { ShowMoreContentCategory } from '@/ui-components/organisms/ShowMoreContentCategory/ShowMoreContentCategory';
import { ItemVariant, LayoutGrid, TileVariant } from '@/ui-components/organisms/ContentCategory/ContentCategory';
import { type EntityCategoryItemProps } from '@/ui-components/molecules/EntityCategoryItem/EntityCategoryItem';
import { useNavSearch } from '@/hooks/useNavSearch';

interface Props {
    searchTerm: string;
    open?: boolean;
    minSearchLength?: number;
    maxSearchLength?: number;
    searchSuggestions?: SearchChip[];
    searchSuggestionsLabel?: string;
    searchQuickLinks?: EntityCategoryItemProps[];
    userTypingDelay?: number;
    onCancelClick?: MouseEventHandler;
    quickLinksHeader?: string;
    domain: string;
    algolia: {
        appID: string;
        apiKey: string;
        indexName: string;
    };
}

export default function NavSearch({
    searchTerm = '',
    open,
    minSearchLength = 3,
    maxSearchLength = 128,
    searchSuggestions = [],
    searchSuggestionsLabel,
    searchQuickLinks = [],
    userTypingDelay = 1000,
    onCancelClick,
    quickLinksHeader,
    domain,
    algolia,
}: Props) {
    const navSearchInputRef = useRef<HTMLInputElement>(null);
    const navSearchRef = useRef<HTMLDivElement>(null);

    const { predictiveResults, searchQuery, isLoading, search, onSuggestionClick, clearSearch } = useNavSearch({
        searchTerm,
        maxSearchLength,
        domain,
        algolia,
    });

    const navSearchId = useId();

    const hasResults = predictiveResults.length > 0;

    const showSuggestions =
        (searchQuery.trim().length < minSearchLength || isLoading) && !hasResults && searchSuggestions.length > 0;

    const showQuickLinks = !hasResults && searchQuickLinks.length > 0;

    // Focus on the input field when the search drawer is opened.
    useEffect(() => {
        if (open && navSearchInputRef.current) {
            navSearchInputRef.current.focus();
        }
    }, [open]);

    // Clear the search input and results when the search drawer is closed.
    useEffect(() => {
        if (!open) {
            clearSearch();
        }
    }, [open, clearSearch]);

    const handleSuggestionClick = (suggestion: string) => {
        onSuggestionClick(suggestion);

        if (navSearchInputRef.current) {
            navSearchInputRef.current.focus();
        }
    };

    const contentCategoryProps = {
        items: searchQuickLinks,
        layoutGrid: 'threeInRow' as LayoutGrid,
        itemVariant: 'list' as ItemVariant,
        tileVariant: 'single' as TileVariant,
    };

    return (
        <div className={styles.searchContainer} ref={navSearchRef} id={navSearchId}>
            <Search
                ref={navSearchInputRef}
                value={searchQuery}
                searchResults={predictiveResults}
                onChange={search}
                isLoading={isLoading}
                minSearchLength={minSearchLength}
                maxSearchLength={maxSearchLength}
                userTypingDelay={userTypingDelay}
                onCancelClick={onCancelClick}
            />

            {showSuggestions && (
                <Grid container className={styles.suggestionsWrapper}>
                    <Grid col={{ lg: 10 }} start={{ lg: 2 }}>
                        <SearchSuggestions
                            title={searchSuggestionsLabel || ''}
                            searchChips={searchSuggestions}
                            onChipSelect={handleSuggestionClick}
                        />
                    </Grid>
                </Grid>
            )}

            {showQuickLinks && (
                <Grid container className={styles.linksWrapper}>
                    <Grid col={{ lg: 10 }} start={{ lg: 2 }}>
                        {quickLinksHeader && (
                            <Text tag="p" className={styles.searchQuickLinksHeading} variant="h4">
                                {quickLinksHeader}
                            </Text>
                        )}
                        <ShowMoreContentCategory content={contentCategoryProps} />
                    </Grid>
                </Grid>
            )}
        </div>
    );
}
